import React from 'react';

export type FeatureShowcaseLayout = 'rows' | 'columns';

interface FeatureShowcaseProps {
  title: string;
  features: Array<{
    title: string;
    description: string;
    image: string;
  }>;
  layout?: FeatureShowcaseLayout;
  className?: string;
}

const normalizeLayout = (layout?: FeatureShowcaseLayout | string | null): FeatureShowcaseLayout =>
  layout === 'columns' ? 'columns' : 'rows';

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({
  title,
  features,
  layout = 'rows',
  className = '',
}) => {
  if (!features?.length) {
    return null;
  }

  const normalizedLayout = normalizeLayout(layout);

  // Lightbox state for viewing feature images in a focused full-screen overlay
  const [lightboxImage, setLightboxImage] = React.useState<{
    src: string;
    title: string;
  } | null>(null);

  const openLightbox = (src: string, title: string) => {
    setLightboxImage({ src, title });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div className={className}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        {title || 'Featured Offerings'}
      </h2>

      {normalizedLayout === 'columns' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={`${feature.title}-${index}`}
              className="bg-gray-50 rounded-lg p-4 flex flex-col items-center text-center"
            >
              <div className="w-full flex justify-center mb-3">
                <div className="w-[80%] aspect-[4/3] rounded-lg overflow-hidden bg-white relative flex items-center justify-center">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover object-center cursor-zoom-in"
                    onClick={() => openLightbox(feature.image, feature.title)}
                  />
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-600">
                {feature.description || 'No description available'}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-10">
          {features.map((feature, index) => {
            const imageOrderClass = index % 2 === 0 ? 'md:order-1' : 'md:order-2';
            const contentOrderClass = index % 2 === 0 ? 'md:order-2' : 'md:order-1';

            return (
              <div
                key={`${feature.title}-${index}`}
                className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-10 md:items-center"
              >
                <div className={`flex justify-center ${imageOrderClass}`}>
                  <div className="w-full max-w-sm aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover object-center cursor-zoom-in"
                      onClick={() => openLightbox(feature.image, feature.title)}
                    />
                  </div>
                </div>
                <div className={`text-center md:text-left ${contentOrderClass}`}>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description || 'No description available'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox overlay for focused feature image with subtle fade/scale animation */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 animate-fade-in"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] w-full bg-black/80 rounded-lg overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-3 right-3 bg-black/70 text-white text-xs px-3 py-1 rounded-full hover:bg-black"
            >
              Close
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title}
              className="w-full h-full object-contain"
            />
            {lightboxImage.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[11px] text-white px-4 py-2">
                {lightboxImage.title}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureShowcase;
